import { ColDef, INumberFilterParams, ITextFilterParams, ValueGetterParams } from 'ag-grid-community';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { GridCellRendererEnum, PermissionsEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestFormatService } from '@vivli/features/data-requests/infrastructure/service';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useActiveUserContext, useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { DataRequestDateRendererComponent } from './data-request-date-renderer.component';
import { DataRequestStatusRenderer } from './data-request-status-renderer.component';
import { DataRequestTitleRendererComponent } from './data-request-title-renderer.component';
import { DataRequestColumnId } from './data-request-page-request';

// DEV-958: one fixed row height for both grids. The Infinite Row Model needs a fixed height, and the
// client-side grid matches it so the two pages look the same.
export const DATA_REQUEST_ROW_HEIGHT = 48;

// DEV-958: how long a floating filter waits after the last keystroke before it applies, so the
// paged grid does not fetch a page per character.
const FILTER_DEBOUNCE_MS = 300;

interface IDataRequestExportColumn {
    key: string;
    headerName: string;
    getValue: (data: IDataRequestSummary, isAmr: boolean) => string;
}

const getExportStatus = (data: IDataRequestSummary) => {
    if (data.status === DataRequestStatusEnum.RejectedByDataProvider) {
        return 'NotApprovedByDataProvider';
    } else if (data.status === DataRequestStatusEnum.RejectedByVivli) {
        return 'NotApprovedByVivli';
    } else if (data.status === DataRequestStatusEnum.RejectedByIRP) {
        return 'NotApprovedByIRP';
    } else return data.status;
};

// The CSV export columns, in file order. Used by the grid's hidden export columns and by the paged
// grid's server-side export, so both write the same file.
const dataRequestExportColumns: IDataRequestExportColumn[] = [
    { key: 'requestId', headerName: 'Request ID', getValue: (data) => data.idString.replace('Vivli ID: ', '') },
    { key: 'requestTitle', headerName: 'Data Request', getValue: (data) => data.requestTitle },
    { key: 'userName', headerName: 'Requester', getValue: (data) => data.userName || 'Unknown' },
    { key: 'userEmail', headerName: "Requester's Email", getValue: (data) => data.userEmail || 'Unknown' },
    {
        key: 'pi',
        headerName: 'Principal Investigator',
        getValue: (data, isAmr) => (isAmr ? data.amrResearcherDetails?.researcherName : DataRequestFormatService.getLeadResearcher(data))
    },
    {
        key: 'piEmail',
        headerName: 'PI Email',
        getValue: (data, isAmr) => (isAmr ? data.amrResearcherDetails?.researcherEmail : data.leadResearcherTeamMemberDetail?.email)
    },
    { key: 'status', headerName: 'Request Status', getValue: getExportStatus },
    {
        key: 'dateOfLastAction',
        headerName: 'Date of Last Update',
        getValue: (data) => moment(data.updatedDate).format('MM/DD/YY hh:mm a')
    },
    {
        key: 'dataContributorOrganizations',
        headerName: 'Data Contributor Organizations',
        getValue: (data) => Array.from(new Set(data.requestedStudies.map((study) => study.organizationName))).join(';')
    }
];

// DEV-958: the hidden export columns carry their own column ids so they cannot collide with the
// visible columns (requestTitle, userName and status are both an export key and a visible column).
const exportColumnId = (key: string) => `export_${key}`;

export const dataRequestExportColumnKeys = dataRequestExportColumns.map((c) => exportColumnId(c.key));
export const dataRequestExportHeaders = dataRequestExportColumns.map((c) => c.headerName);

// One CSV row, keyed in export column order, for exports built outside ag-grid.
export const mapDataRequestSummaryToExportRow = (data: IDataRequestSummary, isAmr: boolean): Record<string, string> =>
    Object.fromEntries(dataRequestExportColumns.map((c) => [c.key, c.getValue(data, isAmr)]));

export const dataRequestGridCustomComponents = [
    {
        component: DataRequestDateRendererComponent,
        name: GridCellRendererEnum.DataRequestDate
    },
    {
        component: DataRequestStatusRenderer,
        name: GridCellRendererEnum.DataRequestStatus
    },
    {
        component: DataRequestTitleRendererComponent,
        name: GridCellRendererEnum.DataRequestTitle
    }
];

// Case-insensitive text sort for the client-side grid; the paged grid sorts on the server.
const caseInsensitiveComparator = (a: string, b: string) => (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });

const textFilterParams: ITextFilterParams = {
    filterOptions: ['contains'],
    maxNumConditions: 1,
    debounceMs: FILTER_DEBOUNCE_MS
};

const numberFilterParams: INumberFilterParams = {
    filterOptions: ['equals', 'greaterThan', 'lessThan'],
    maxNumConditions: 1,
    debounceMs: FILTER_DEBOUNCE_MS
};

interface UseDataRequestGridColumnsProps {
    isPresumedResearcher?: boolean;
    initialSortField?: string;
    initialSortOrder?: 'asc' | 'desc';
}

// The column definitions shared by the client-side and the paged Data Requests grids.
// DEV-958: six sortable, filterable columns instead of the two card columns. Every valueGetter
// guards against a missing row: the Infinite Row Model renders placeholder rows with no data.
export const useDataRequestGridColumns = ({
    isPresumedResearcher,
    initialSortField,
    initialSortOrder
}: UseDataRequestGridColumnsProps): ColDef[] => {
    const { t } = useTranslation();
    const duaTranslation = t(TranslationKeyEnum.duaValidatedStatusString);
    const { isAmr } = useAssignedAppTypeContext();
    const { user, hasPermission, orgHasPermission } = useActiveUserContext();

    // A data contributor may not see who requested a Draft against its studies, nor its lead researcher;
    // everyone else may. DEV-958: existing rule, moved unchanged from the card's title renderer (where it
    // was canSeeCreator and hid both lines) now that each name is a column of its own; only the null
    // guards are new.
    const canSeeRequesterAndLead = (dataRequest: IDataRequestSummary) => {
        //TODO PERMFE - redo with permissions+
        if (dataRequest.status !== DataRequestStatusEnum.Draft || hasPermission(PermissionsEnum.DrActAdv)) {
            return true;
        }

        const studyOrgIds = dataRequest.requestedStudies?.map((x) => x.organizationId) ?? [];
        //TODO PERMFE - redo with permissions+
        const dataProviderOrgIds =
            user?.orgMemberships
                ?.filter((x) => orgHasPermission(x.orgId, PermissionsEnum.DataContrib) && studyOrgIds.includes(x.orgId))
                .map((x) => x.orgId) ?? [];
        return dataProviderOrgIds.length === 0;
    };

    const exportCols: ColDef[] = dataRequestExportColumns.map((c) => ({
        colId: exportColumnId(c.key),
        headerName: c.headerName,
        hide: true,
        valueGetter: (params: ValueGetterParams<IDataRequestSummary>) => (params.data ? c.getValue(params.data, isAmr) : null)
    }));

    const hasCustomSort = !!initialSortField;
    const sortOrder = initialSortOrder || 'desc';

    return [
        ...(hasCustomSort
            ? [
                  {
                      field: initialSortField,
                      hide: true,
                      sortable: true,
                      initialSort: sortOrder as 'asc' | 'desc'
                  }
              ]
            : []),
        {
            colId: DataRequestColumnId.doi,
            field: 'dataRequestDoi',
            headerName: 'Data Request ID',
            sortable: true,
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
            maxWidth: 170,
            ...(!hasCustomSort ? { initialSort: 'desc' as const } : {})
        },
        {
            colId: DataRequestColumnId.title,
            field: 'requestTitle',
            headerName: 'Data Request Title',
            sortable: true,
            comparator: caseInsensitiveComparator,
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
            cellRenderer: GridCellRendererEnum.DataRequestTitle,
            flex: 3
        },
        {
            colId: DataRequestColumnId.studyCount,
            headerName: 'Total Studies',
            valueGetter: (params: ValueGetterParams<IDataRequestSummary>) =>
                params.data ? (params.data.requestedStudies?.length ?? 0) : null,
            sortable: true,
            filter: 'agNumberColumnFilter',
            filterParams: numberFilterParams,
            maxWidth: 150
        },
        {
            colId: DataRequestColumnId.leadResearcher,
            headerName: 'Lead Researcher',
            hide: isAmr,
            valueGetter: (params: ValueGetterParams<IDataRequestSummary>) => {
                if (!params.data) {
                    return null;
                }
                return canSeeRequesterAndLead(params.data) ? DataRequestFormatService.getLeadResearcher(params.data) : '';
            },
            sortable: true,
            comparator: caseInsensitiveComparator,
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams
        },
        {
            colId: DataRequestColumnId.requester,
            field: 'userName',
            headerName: 'Requester',
            valueGetter: (params: ValueGetterParams<IDataRequestSummary>) => {
                if (!params.data) {
                    return null;
                }
                return canSeeRequesterAndLead(params.data) ? params.data.userName || 'Unknown' : '';
            },
            sortable: true,
            comparator: caseInsensitiveComparator,
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams
        },
        {
            colId: DataRequestColumnId.status,
            field: 'status',
            headerName: 'Status',
            valueGetter: (params: ValueGetterParams<IDataRequestSummary>) =>
                params.data ? DataRequestFormatService.mapStatusToMessage(params.data.status, isAmr, duaTranslation) : null,
            sortable: true,
            comparator: caseInsensitiveComparator,
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
            cellRenderer: GridCellRendererEnum.DataRequestStatus,
            cellRendererParams: { isPresumedResearcher },
            flex: 2
        },
        ...exportCols
    ];
};
